import { useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { showToast } from "@/components/ui/toast";
import { PROTECTED_ROUTES } from "@/constants/routes";
import {
  ProfileHeaderCard,
  ProfileHeaderCardSkeleton,
} from "@/features/profile/components/ProfileHeaderCard";
import { useMyProfile } from "@/features/profile/hooks/useMyProfile";
import { useUpdateProfile, useUploadAvatar } from "@/features/profile/hooks/useUpdateProfile";
import { editProfileSchema, type EditProfileValues } from "@/features/profile/schemas/profile.schemas";
import { ApiError } from "@/types/api";

/**
 * docs/ui/routes.md `/profile` — own profile, editable. Read-only viewing
 * of someone *else's* profile is a separate page/route
 * (`PublicProfilePage`, `/profile/:userId`) — never this one; there's no
 * `:userId` param here at all, so it's structurally impossible to point
 * this page at another user's data.
 */
export function ProfilePage() {
  const profileQuery = useMyProfile();
  const updateProfileMutation = useUpdateProfile();
  const uploadAvatarMutation = useUploadAvatar();
  const [isEditing, setIsEditing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditProfileValues>({
    resolver: zodResolver(editProfileSchema),
    values: profileQuery.data
      ? {
          firstName: profileQuery.data.firstName,
          lastName: profileQuery.data.lastName,
          bio: profileQuery.data.bio,
          city: profileQuery.data.location?.city ?? "",
          state: profileQuery.data.location?.state ?? "",
        }
      : undefined,
  });

  function handleAvatarFileSelected(file: File | undefined) {
    if (!file) return;
    uploadAvatarMutation.mutate(file, {
      onError: (error) => {
        showToast.error(error instanceof ApiError ? error.message : "Couldn't update photo.");
      },
    });
  }

  function onSubmit(values: EditProfileValues) {
    updateProfileMutation.mutate(values, {
      onSuccess: () => {
        showToast.success("Profile updated.");
        setIsEditing(false);
      },
      onError: (error) => {
        showToast.error(error instanceof ApiError ? error.message : "Couldn't update profile.");
      },
    });
  }

  if (profileQuery.isPending) {
    return (
      <div className="mx-auto max-w-[640px] space-y-6 px-4 py-8">
        <ProfileHeaderCardSkeleton />
      </div>
    );
  }

  if (profileQuery.isError || !profileQuery.data) {
    return (
      <div className="mx-auto max-w-[640px] px-4 py-8">
        <p className="text-body text-muted-foreground">Couldn&rsquo;t load your profile.</p>
      </div>
    );
  }

  const profile = profileQuery.data;

  return (
    <div className="mx-auto max-w-[640px] space-y-6 px-4 py-8">
      <ProfileHeaderCard
        name={profile.name}
        avatarUrl={profile.avatarUrl}
        memberSince={profile.memberSince}
        bio={isEditing ? undefined : profile.bio}
        isOwn
        onEditAvatar={() => fileInputRef.current?.click()}
        isUploadingAvatar={uploadAvatarMutation.isPending}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="sr-only"
        onChange={(event) => handleAvatarFileSelected(event.target.files?.[0])}
      />

      <div className="rounded-lg border border-border bg-card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-h4 text-foreground">Account details</h2>
          {!isEditing && (
            <Button type="button" variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
              Edit
            </Button>
          )}
        </div>

        {!isEditing ? (
          <dl className="space-y-3 text-body-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Email</dt>
              <dd className="text-foreground">{profile.email}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Phone</dt>
              <dd className="text-foreground">{profile.phone}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Location</dt>
              <dd className="text-foreground">
                {profile.location ? `${profile.location.city}, ${profile.location.state}` : "Not set"}
              </dd>
            </div>
          </dl>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">First name</Label>
                <Input id="firstName" {...register("firstName")} />
                {errors.firstName && (
                  <p className="text-caption text-destructive" role="alert">
                    {errors.firstName.message}
                  </p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Last name</Label>
                <Input id="lastName" {...register("lastName")} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="bio">Bio</Label>
              <Textarea id="bio" rows={3} {...register("bio")} />
              {errors.bio && (
                <p className="text-caption text-destructive" role="alert">
                  {errors.bio.message}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="city">City</Label>
                <Input id="city" {...register("city")} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="state">State</Label>
                <Input id="state" {...register("state")} />
              </div>
            </div>

            <div className="flex gap-2">
              <Button type="submit" isLoading={updateProfileMutation.isPending}>
                Save changes
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  reset();
                  setIsEditing(false);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </div>

      <Link
        to={PROTECTED_ROUTES.myListings}
        className="block rounded-lg border border-border bg-card p-4 text-center text-body font-medium text-primary hover:bg-muted"
      >
        Manage your listings →
      </Link>
    </div>
  );
}
